import { config } from "../../config";
import { SubledgerEndpoints } from "../../SubledgerEndpoints";
import { authPatch, authDelete } from "../../services/RequestHelpers";
import { ISlEntryAttachmentActionRequest } from "../../schemas/SLAttachmentPostBody";
import { ISLEntryActionResponse } from "../../schemas/ISLEntryResponse";
import { ISlEntryActionRequest, ISlEntryPosterActionRequest } from "../../schemas/SlEntryActionRequest";
import { ISLAdditionalPostersRequest } from "../../schemas/SLAdditionalPostersRequest";
import { StatusCodesToString } from "../../schemas/ISLEntryStatusCodes";
import { SLEntriesError } from "../../services/ErrorHelpers";
import { IProcessingStatus } from "../SLEntriesDetails";

import { IUserProviderState } from "../../contexts/UserContext";
import { mergeProcessingStatuses } from "./utilities";
import { leftPadDocumentNumber } from "../../utilities/utilities";

export async function actionsClientCalls(
  data:
    | ISlEntryActionRequest
    | ISLAdditionalPostersRequest
    | ISlEntryAttachmentActionRequest
    | ISlEntryPosterActionRequest,
  url: string,
  getTokenFn: IUserProviderState["accessToken"]
): Promise<ISLEntryActionResponse[]> {
  const result = await authPatch(url, getTokenFn, data);
  return result.data as ISLEntryActionResponse[];
}

export interface ICommandBarRequestsForTextField {
  AddPosters: (entryIds: number[], text: string) => Promise<IProcessingStatus>;
  SendToPoster: (entryIds: number[], text: string) => Promise<IProcessingStatus>;
  NeedClarification: (entryIds: number[], text: string) => Promise<IProcessingStatus>;
}

export interface ICommandBarRequestsForCheckbox {
  Post: (entryIds: number[]) => Promise<IProcessingStatus>;
  ApproveEntries: (entryIds: number[]) => Promise<IProcessingStatus>;
  RecallEntries: (entryIds: number[], posterIsRecallingSignedOffRequest: boolean) => Promise<IProcessingStatus>;
  Delete: (entryIds: number[], batchRefGuids: string[]) => Promise<IProcessingStatus>;
  DeleteAttachments: (entryIds: number[]) => Promise<IProcessingStatus>;
}

export enum ErrorMessages {
  FollItemsNeedClarError = "Following could not be updated to Needs Clarification. ",
  FollItemsAddPosters = "Failed to add posters to following entries. ",
  FollItemsApprove = "Failed to approve following entries. ",
  FollItemsRecall = "Failed to recall following entries",
  FollItemsAddAttachment = "Failed to attach file/s for following entries",
  AddPosterException = "Unable to Add Posters - changes not saved. ",
  ApproveEntriesException = "Unable to Approve - changes not saved. ",
  NeedsClarificationException = "Unable to update to Needs Clarification - changes not saved. ",
  RecallEntriesException = "Unable to recall - changes not saved. ",
  FollItemsDeleteDrafts = "Failed to delete the following entries.",
  DeleteEntriesException = "Unable to Delete -  changes not saved.",
  FollItemsSendTOPoster = "Following entries could not be sent to poster.",
  SendToPosterException = "Unable to Send to Poster - changes not saved.",
  AddAttachmentException = "Unable to Add Attachment - changes not saved."
}

function handleActionMessages(responseForEntriesAction: ISLEntryActionResponse[]): IProcessingStatus {
  if (responseForEntriesAction.length === 1) {
    if (responseForEntriesAction[0].errorMessage !== null && !responseForEntriesAction[0].slEntryId) {
      throw new SLEntriesError([responseForEntriesAction[0].errorMessage]);
    }
  }
  const payload: IProcessingStatus = responseForEntriesAction.reduce(
    (ctr, entry) => {
      if (entry.errorMessage) {
        if (entry.slEntryId in ctr && ctr.statuses[entry.slEntryId].type !== "success") {
          ctr.statuses[entry.slEntryId].messages.push(entry.errorMessage);
        } else {
          ctr.statuses[entry.slEntryId] = {
            type: "error",
            messages: [entry.errorMessage],
            nameDocumentId: leftPadDocumentNumber(entry.slEntryId)
          };
          ctr.errors += 1;
        }
      } else {
        ctr.statuses[entry.slEntryId] = {
          type: "success",
          messages: [StatusCodesToString[entry.slNewStatus as keyof typeof StatusCodesToString]],
          nameDocumentId: leftPadDocumentNumber(entry.slEntryId)
        };
        ctr.successes += 1;
      }
      return ctr;
    },
    {
      errors: 0,
      successes: 0,
      statuses: {}
    } as IProcessingStatus
  );
  return payload;
}

export const textFieldActions = (getTokenFn: IUserProviderState["accessToken"]): ICommandBarRequestsForTextField => ({
  AddPosters: async (SelectedSlEntryIds: number[], text: string) => {
    const PosterAliases = text.split(";").filter((isNotEmpty) => isNotEmpty);
    const url = config.get("subledgerUrl") + SubledgerEndpoints.slActions + SubledgerEndpoints.slActionsAddPosters;
    const response = await actionsClientCalls(
      {
        SelectedSlEntryIds,
        PosterAliases
      },
      url,
      getTokenFn
    );
    return handleActionMessages(response);
  },
  SendToPoster: async (SelectedSlEntryIds: number[], text: string) => {
    const PosterAliases = text;
    const url = config.get("subledgerUrl") + SubledgerEndpoints.slActions + SubledgerEndpoints.slActionsSendToPoster;
    const response = await actionsClientCalls(
      {
        SelectedSlEntryIds,
        PosterAliases
      },
      url,
      getTokenFn
    );
    return handleActionMessages(response);
  },
  NeedClarification: async (SelectedSlEntryIds: number[], text: string) => {
    const url =
      config.get("subledgerUrl") + SubledgerEndpoints.slActions + SubledgerEndpoints.slActionsNeedsClarification;
    const response = await actionsClientCalls(
      {
        SelectedSlEntryIds,
        Comments: text
      },
      url,
      getTokenFn
    );
    return handleActionMessages(response);
  }
});

export const checkBoxActions = (getTokenFn: IUserProviderState["accessToken"]): ICommandBarRequestsForCheckbox => {
  const deleteSLBatches = async (SelectedSlRefGuids: string[]) => {
    const urls = SelectedSlRefGuids.map(
      (refGuid) => config.get("subledgerUrl") + SubledgerEndpoints.slBatchDetails.replace("{refguid}", refGuid)
    );
    if (urls.length !== 0) {
      // THIS API RETURNS A NON STANDARD BOOLEAN (TRUE OR FALSE)
      // IF THIS CHANGES THE FOLLOWING CODE WILL BREAK
      const deletes = urls.map((url) => authDelete(url, getTokenFn, {}));
      const responses = await Promise.all(deletes);
      const responsesResults = responses.map((r) => r.data as boolean);
      return responsesResults.reduce(
        (ctr, response) => {
          if (response) {
            ctr.successes += 1;
          } else {
            ctr.errors += 1;
          }
          return ctr;
        },
        {
          errors: 0,
          successes: 0,
          statuses: {}
        } as IProcessingStatus
      );
    } else {
      return handleActionMessages([]);
    }
  };
  const deleteSLEntries = async (SelectedSlEntryIds: number[]) => {
    const url = config.get("subledgerUrl") + SubledgerEndpoints.slActions + SubledgerEndpoints.slActionsDeleteDrafts;
    if (SelectedSlEntryIds.length !== 0) {
      const response = await authDelete(url, getTokenFn, {
        SelectedSlEntryIds
      });
      if (Array.isArray(response.data)) {
        return handleActionMessages(response.data as ISLEntryActionResponse[]);
      } else {
        if (typeof response.data === "boolean") {
          if (response.data) {
            return {
              errors: 0,
              successes: 1,
              statuses: {}
            };
          } else {
            return {
              errors: 1,
              successes: 0,
              statuses: {}
            };
          }
        }
        return handleActionMessages([]);
      }
    } else {
      return handleActionMessages([]);
    }
  };
  const basicImplementation = async (SelectedSlEntryIds: number[], endpoint: string) => {
    const url = config.get("subledgerUrl") + SubledgerEndpoints.slActions + endpoint;
    const response = await actionsClientCalls(
      {
        SelectedSlEntryIds
      },
      url,
      getTokenFn
    );
    return handleActionMessages(response);
  };
  return {
    Post: (SelectedSlEntryIds: number[]) => basicImplementation(SelectedSlEntryIds, SubledgerEndpoints.slActionsPost),
    ApproveEntries: (SelectedSlEntryIds: number[]) =>
      basicImplementation(SelectedSlEntryIds, SubledgerEndpoints.slActionsApproveEntries),
    RecallEntries: async (SelectedSlEntryIds: number[], posterIsRecallingSignedOffRequest: boolean) => {
      // If at least one entry has a status of 10 then use "Poster is recalling Signed Off" Endpoint
      // Otherwise use the "Recalling Draft" endpoint
      // On Dashboard statusId is the same for all entries
      // On Search statusId might be different, but no drafts will show up, so the only valid action is Signed Off
      // API correctly catches wrong Statuses
      const endpoint = posterIsRecallingSignedOffRequest
        ? SubledgerEndpoints.slActionsPosterIsRecallingSignedOffRequested
        : SubledgerEndpoints.slActionsAuthorIsRecallingDraft;
      return basicImplementation(SelectedSlEntryIds, endpoint);
    },
    Delete: async (SelectedSlEntryIds: number[], SelectedSlRefGuids: string[]) => {
      const processingStatusesFromEntries = await deleteSLEntries(SelectedSlEntryIds);
      const processingStatusesFromBatches = await deleteSLBatches(SelectedSlRefGuids);
      return mergeProcessingStatuses(processingStatusesFromEntries, processingStatusesFromBatches);
    },
    DeleteAttachments: async (SelectedSlEntryIds: number[]) => {
      const url =
        config.get("subledgerUrl") + SubledgerEndpoints.slActions + SubledgerEndpoints.slActionsDeleteAttachments;
      if (SelectedSlEntryIds.length !== 0) {
        const response = await authDelete(url, getTokenFn, {
          SelectedSlEntryIds
        });
        if (Array.isArray(response.data)) {
          if (response.data.length === 0) {
            return {
              errors: 0,
              successes: SelectedSlEntryIds.length,
              statuses: {}
            };
          }
          return handleActionMessages(response.data as ISLEntryActionResponse[]);
        } else {
          if (typeof response.data === "boolean") {
            if (response.data) {
              return {
                errors: 0,
                successes: 1,
                statuses: {}
              };
            } else {
              return {
                errors: 1,
                successes: 0,
                statuses: {}
              };
            }
          }
          return handleActionMessages([]);
        }
      } else {
        return handleActionMessages([]);
      }
    }
  };
};
